// API helper for the Emergency Department module.
//
// Tenant headers are sent explicitly on every call, matching the convention in
// src/pages/ipd/api/emergencyToIpdApi.js and ipdEnhancementsApi.js.
//
// Note there is deliberately no facility_id parameter anywhere in here: the
// backend resolves the caller's facility from their own authenticated account
// and refuses any request that names a different one, so sending it from the
// client would be both redundant and untrusted.

import axios from "axios";

export const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

export function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

/**
 * The backend answers both success and business failures with HTTP 200 and a
 * `status` field, so a caller cannot rely on the HTTP status alone. Unwrap it
 * once here and throw on failure, so screens can use plain try/catch.
 */
export function unwrap(res) {
  const body = res?.data;
  if (!body || body.status !== "Success") {
    throw new Error(body?.message || "Something went wrong");
  }
  return body;
}

/**
 * Emergency visits for the logged-in user's facility.
 * @param {"open"|"closed"} [status] omit for both
 */
export async function listEmergencyVisits({ status, limit, search } = {}) {
  const params = {};
  if (status) params.status = status;
  if (limit) params.limit = limit;
  if (search) params.search = search;

  const res = await axios.get(`${BASE}emergency/visits/`, {
    headers: authHeaders(),
    params,
  });
  const body = unwrap(res);
  return { visits: body.data || [], count: body.count ?? 0 };
}

/**
 * Assign or re-assign a triage category (ER-TRI-*).
 * Each call appends to the visit's triage history; nothing is overwritten.
 */
export async function triageEmergencyVisit(visitId, { category, note } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/triage/`,
    { category, ...(note ? { note } : {}) },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * What the assign-bed dialog can offer: free beds in active categories, and
 * the doctors who can be named. Deliberately server-filtered — offering a bed
 * the assignment would then refuse is worse than not offering it.
 */
export async function getAssignable() {
  const res = await axios.get(`${BASE}emergency/assignable/`, { headers: authHeaders() });
  return unwrap(res).data;
}

/**
 * Place a patient in a bed and name their Emergency Provider. Both are
 * required — a bed is never assigned on its own (ER-BED-002).
 */
export async function assignBed(visitId, { bedId, providerId, reason } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/bed/`,
    { bed_id: bedId, provider_id: providerId, ...(reason ? { reason } : {}) },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * The state of every emergency bed for the Bed Management screen (ticket 12):
 * free/occupied/cleaning counts per category, each bed with its status, and who
 * occupies the occupied ones with their Emergency Provider.
 */
export async function getBedOccupancy() {
  const res = await axios.get(`${BASE}emergency/occupancy/`, { headers: authHeaders() });
  return unwrap(res).data;
}

/**
 * Mark a bed that was being cleaned as clean — it reads free afterwards. Only a
 * bed in the cleaning state can be marked; the server refuses anything else.
 */
export async function markBedClean(bedId) {
  const res = await axios.post(
    `${BASE}emergency/beds/${bedId}/clean/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Hand over the Emergency Provider on an open visit (ER-PRV-003).
 *
 * Distinct from assignBed: the patient keeps their bed and only the doctor
 * clinically responsible changes — a shift change or a case passed to someone
 * else. Each call appends to the visit's provider history; no earlier provider
 * is overwritten.
 */
export async function changeEmergencyProvider(visitId, { providerId, reason } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/provider/`,
    { provider_id: providerId, ...(reason ? { reason } : {}) },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { visit: body.data, message: body.message };
}

/**
 * Record or correct the clinical assessment (ER-ASM-001/006).
 *
 * One call for both, because from the provider's side there is one action:
 * write down the current picture. Only send the fields that changed — omitted
 * ones keep their value, an empty string clears one.
 */
export async function saveAssessment(visitId, fields) {
  const res = await axios.put(
    `${BASE}emergency/visits/${visitId}/assessment/`,
    fields,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { visit: body.data, assessment: body.assessment, message: body.message };
}

/**
 * Place one or more orders on a visit (ER-ORD-001…006).
 *
 * A list rather than one call per item, because ordering a panel of bloods is
 * one decision and six round trips would let half of them land.
 *
 * The response reports `sent` and `failed`: the emergency record is written
 * whatever the laboratory, imaging or pharmacy system does, so an order that
 * did not arrive shows as unsent rather than being assumed to have worked.
 */
export async function placeOrders(visitId, orders, { orderingDoctorId } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/orders/`,
    {
      orders,
      // The doctor the basket is prescribed for. A nurse acting on a verbal
      // order names them; omitted, the backend falls back to the visit's
      // Emergency Provider.
      ...(orderingDoctorId ? { ordering_doctor_id: orderingDoctorId } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return {
    orders: body.data || [],
    sent: body.sent,
    failed: body.failed,
    // Lines the server would not accept at all, each naming the one it refused
    // by its 1-based position in the basket that was sent. Distinct from
    // `failed`, which counts orders that were placed but did not reach the
    // receiving department.
    rejected: body.rejected || [],
    message: body.message,
  };
}

/**
 * Fuzzy medicine search for the structured Rx picker (ER-ORD medication
 * ordering). GET search_medicines/?name=<q> -> { data: [{ conceptId, term, id }] }.
 * Reused verbatim from the hospital's medicine formulary search.
 */
export async function searchMedicines(query) {
  const q = (query || "").trim();
  if (!q) return [];
  const res = await axios.get(
    `${BASE}search_medicines/?name=${encodeURIComponent(q)}`,
    { headers: authHeaders() }
  );
  const rows = res?.data?.data || [];
  return rows
    .map((m) => ({
      id: m.id ?? m.conceptId ?? m.term,
      term: m.term || m.conceptFsn || "",
      conceptId: m.conceptId ?? null,
    }))
    .filter((m) => m.term);
}

/**
 * Fuzzy search of the hospital's medical service catalogue, for the lab and
 * imaging order picker. GET search_medical_services/?name=<q>&episode_type=OPD
 * -> { status, data: [{ medical_service_id, term, service_type, ... }] }.
 *
 * episode_type OPD because an emergency patient is not admitted. Nothing here
 * distinguishes a blood test from a scan: the backend reads the chosen
 * service's own group in the catalogue and files the order accordingly, so a
 * picker that guessed would only ever be able to guess wrong.
 *
 * Degrades to an empty list rather than throwing — an unreachable catalogue
 * leaves the field empty, it does not take the orders card down.
 */
export async function searchMedicalServices(query) {
  const q = (query || "").trim();
  if (!q) return [];
  const res = await axios.get(
    `${BASE}search_medical_services/?name=${encodeURIComponent(q)}&episode_type=OPD`,
    { headers: authHeaders() }
  );
  const rows = res?.data?.data || [];
  return rows
    .map((s) => ({
      medicalServiceId: s.medical_service_id ?? s.id,
      term: s.term || s.conceptFsn || "",
      serviceType: s.service_type || "",
      serviceGroupId: s.service_group_id,
    }))
    .filter((s) => {
      if (!s.term || !s.medicalServiceId) return false;
      const isRadiology =
        s.serviceGroupId === 1 ||
        (s.serviceType && String(s.serviceType).toUpperCase().includes("RAD"));
      const isLab =
        s.serviceGroupId === 10 ||
        (!isRadiology && s.serviceGroupId != null);
      return isRadiology || isLab;
    });
}

/**
 * The medicine frequency master for the Rx frequency dropdown, deduped by code
 * (the master repeats each code across medicine types).
 */
export async function getMedicineFrequencies() {
  const res = await axios.post(
    `${BASE}get_medicine_frequencies/`,
    { medicine_type: "" },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const rows = res?.data?.frequencies || [];
  const seen = new Map();
  rows.forEach((f) => {
    const code = f.frequency_code;
    if (code && !seen.has(code)) seen.set(code, { code, label: f.full_form || code });
  });
  return Array.from(seen.values());
}

/** Read-only clinical snapshot of a visit (diagnostic doctor review). */
export async function getVisitCondition(visitId) {
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/condition/`, {
    headers: authHeaders(),
    skipGlobalLoader: true,
  });
  return unwrap(res).data || null;
}

/** The visit's orders, urgent first (ER-ORD-005). */
export async function listOrders(visitId) {
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/orders/`, {
    headers: authHeaders(),
  });
  return unwrap(res).data || [];
}

/**
 * Predefined order sets offered by the pharmacy CPOE service (ticket 17).
 *
 * These are proxied from an external pharmacy service, so the response shape is
 * owned there, not here — parsed defensively across the likely envelopes until
 * the contract is confirmed. The service is not configured in every environment;
 * a failure surfaces as the dialog's error state.
 */
export async function listOrderSets(params = {}) {
  const res = await axios.get(`${BASE}inventory/pharmacy/order-sets/`, {
    headers: authHeaders(),
    params,
  });
  const body = unwrap(res);
  return body.data || body.order_sets || body.results || [];
}

/** Expand one order set into its items. Shape owned by the pharmacy service. */
export async function expandOrderSet(orderSetId) {
  const res = await axios.get(
    `${BASE}inventory/pharmacy/order-sets/${orderSetId}/expand/`,
    { headers: authHeaders() }
  );
  const body = unwrap(res);
  return body.data || body.items || body.order_set || body;
}

/**
 * Record one dose actually given during the visit (ER-MED-001…003).
 *
 * A log of what happened, not a plan for what to give — there is deliberately
 * no due time or schedule (ER-MED-002). `orderId` links the dose to the
 * medicine order it fulfilled where there is one (ER-MED-003); omit it for a
 * dose given without a matching prescription (ER-MED-001).
 *
 * `stock` ({itemId, itemName, batchNumber, quantity}) names the pharmacy item
 * the dose came from, which charges it; the server prices it and still records
 * the dose if pricing fails, answering with `chargeWarning`.
 */
export async function recordMedication(
  visitId,
  { medicineName, dose, route, orderId, administeredAt, note, stock } = {}
) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/medications/`,
    {
      medicine_name: medicineName,
      dose,
      route,
      ...(orderId ? { order_id: orderId } : {}),
      ...(administeredAt ? { administered_at: administeredAt } : {}),
      ...(note ? { note } : {}),
      ...(stock
        ? {
            item_id: stock.itemId,
            item_name: stock.itemName,
            batch_number: stock.batchNumber,
            quantity: stock.quantity,
          }
        : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { administration: body.data, message: body.message, chargeWarning: body.charge_warning || "" };
}

/** The visit's administered doses, most-recent-first (ER-MED-001). */
export async function listMedications(visitId) {
  const res = await axios.get(
    `${BASE}emergency/visits/${visitId}/medications/`,
    { headers: authHeaders() }
  );
  return unwrap(res).data || [];
}

/**
 * Record one set of vital signs on the visit (ER-TRI-005). Send only the
 * readings taken; a set with no reading at all is refused. Out-of-range values
 * are stored, not rejected — the highlighting of ER-TRI-006 is done on display.
 */
export async function recordVitals(visitId, readings) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/vitals/`,
    readings,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { vitals: body.data, message: body.message };
}

/** The visit's vital-sign sets, most-recent-first (ER-TRI-005). */
export async function listVitals(visitId) {
  const res = await axios.get(
    `${BASE}emergency/visits/${visitId}/vitals/`,
    { headers: authHeaders() }
  );
  return unwrap(res).data || [];
}

/* ------------------------------------------------------------------ */
/* Clinical safety — allergies, risks, home medications (ticket 15)    */
/* ------------------------------------------------------------------ */

/** Record an allergy or adverse reaction against the visit. */
export async function addAllergy(visitId, { allergen, reaction, severity } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/allergies/`,
    { allergen, ...(reaction ? { reaction } : {}), ...(severity ? { severity } : {}) },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/** Remove an allergy entered in error. */
export async function removeAllergy(visitId, allergyId) {
  const res = await axios.delete(
    `${BASE}emergency/visits/${visitId}/allergies/${allergyId}/`,
    { headers: authHeaders() }
  );
  return unwrap(res);
}

/**
 * Record the clinical-risk assessment — fall risk, pressure-ulcer risk, pain
 * score. One row per visit, edited in place; the latest values are shown. Send
 * the whole form: an empty string clears a field, and the server refuses a save
 * that would leave nothing recorded.
 */
export async function saveRiskAssessment(
  visitId,
  { fallRisk, pressureUlcerRisk, painScore } = {}
) {
  const res = await axios.put(
    `${BASE}emergency/visits/${visitId}/risk-assessment/`,
    {
      fall_risk: fallRisk || "",
      pressure_ulcer_risk: pressureUlcerRisk || "",
      pain_score: painScore === "" || painScore == null ? "" : Number(painScore),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Record a medicine the patient was taking before arriving. A record only —
 * nothing is checked against prescriptions or orders.
 */
export async function addHomeMedication(
  visitId,
  { medicineName, dose, frequency, note } = {}
) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/home-medications/`,
    {
      medicine_name: medicineName,
      ...(dose ? { dose } : {}),
      ...(frequency ? { frequency } : {}),
      ...(note ? { note } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/** Remove a home medication from the list. */
export async function removeHomeMedication(visitId, medId) {
  const res = await axios.delete(
    `${BASE}emergency/visits/${visitId}/home-medications/${medId}/`,
    { headers: authHeaders() }
  );
  return unwrap(res);
}

/**
 * Draft the discharge summary from the visit's own record (ER-DSP-013).
 *
 * Also the regenerate call: it deliberately discards any edits, which is the
 * point of offering it. The response says whether it did, so the screen can
 * warn before rather than apologise after.
 */
export async function draftDischargeSummary(visitId) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/discharge-summary/`,
    {},
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return {
    summary: body.data,
    replacedEdits: body.replaced_edits,
    message: body.message,
  };
}

/** Save the doctor's edits to the draft. Only send the sections that changed. */
export async function saveDischargeSummary(visitId, sections) {
  const res = await axios.put(
    `${BASE}emergency/visits/${visitId}/discharge-summary/`,
    sections,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { summary: body.data, message: body.message };
}

/**
 * Record a mid-visit stage event — "observation" (ER-DSP-012a). It does not
 * end the visit; it stays open and still reaches a disposition. (The "theatre"
 * event was removed in ticket 29 — surgery goes via inpatient admission.)
 */
export async function recordStageEvent(visitId, { event, note } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/stage-event/`,
    { event, ...(note ? { note } : {}) },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Flag or unflag a visit as medico-legal (ER-MLC-001/002).
 * Unflagging keeps the case number — re-flagging restores the same one.
 */
export async function setMlcFlag(visitId, flagged) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/mlc/`,
    { flagged },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { visit: body.data, mlc: body.mlc, message: body.message };
}

/** Attendant details and police intimation on a flagged visit. */
export async function updateMlcDetail(visitId, patch) {
  const res = await axios.patch(
    `${BASE}emergency/visits/${visitId}/mlc/`,
    patch,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return { visit: body.data, mlc: body.mlc };
}

/** Add an injury to a medico-legal visit's report. Returns the whole report. */
export async function addInjury(visitId, { bodyPartId, woundTypeId, description }) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/injuries/`,
    {
      body_part_id: bodyPartId,
      wound_type_id: woundTypeId,
      ...(description ? { description } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/** Remove an injury. Returns the remaining report. */
export async function removeInjury(visitId, injuryId) {
  const res = await axios.delete(
    `${BASE}emergency/visits/${visitId}/injuries/${injuryId}/`,
    { headers: authHeaders() }
  );
  return unwrap(res).data;
}

/**
 * Close a visit with one of six outcomes (ER-DSP-*). Frees the patient's bed
 * under the hospital's housekeeping rule. A closed visit is never reopened.
 *
 * An "admitted" outcome must carry `admitting_doctor_id`: it refers the
 * patient into the IPD registration queue (the same queue an OPD referral
 * lands in) under that doctor; the admission desk completes registration,
 * chooses the bed and creates the inpatient admission from the wizard.
 * Referral and closure succeed or fail together, so a rejection here means
 * the patient is still in the department.
 */
export async function closeEmergencyVisit(visitId, payload) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/close/`,
    payload,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  const body = unwrap(res);
  return {
    visit: body.data,
    disposition: body.disposition,
    ipdReferral: body.ipd_referral,
    message: body.message,
  };
}

/**
 * Certify a death — the Emergency Provider attests it, which makes the death
 * record valid (emergency-death-registration). The death record is opened when a
 * visit closes as "expired". The certifying doctor defaults to the visit's
 * Emergency Provider; pass certifyingDoctorId for a brought-in-dead arrival that
 * never had one. Optionally correct the time of death.
 */
export async function certifyDeath(visitId, { certifyingDoctorId, deathDatetime } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/death/certify/`,
    {
      ...(certifyingDoctorId ? { certifying_doctor_id: certifyingDoctorId } : {}),
      ...(deathDatetime ? { death_datetime: deathDatetime } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Record the MCCD cause of death on the death record, or mark it pending
 * (emergency-death-registration ticket 02). Either a completed Part I causal
 * chain, or `causePending` with a reason — the server refuses an empty non-
 * pending cause. `part1` is [{line:'a'|'b'|'c', condition, interval}]; `part2`
 * is [{condition, interval}].
 */
export async function recordDeathCause(
  visitId,
  {
    manner,
    part1,
    part2,
    injuryDescription,
    pregnancyAssociated,
    deliveryOccurred,
    causePending,
    causePendingReason,
  } = {}
) {
  const body = {
    ...(manner ? { manner_of_death: manner } : {}),
    ...(injuryDescription ? { mccd_injury_description: injuryDescription } : {}),
    ...(pregnancyAssociated != null ? { mccd_pregnancy_associated: pregnancyAssociated } : {}),
    ...(deliveryOccurred != null ? { mccd_delivery_occurred: deliveryOccurred } : {}),
  };
  if (causePending) {
    body.cause_pending = true;
    body.cause_pending_reason = causePendingReason;
  } else {
    body.mccd_part1 = part1 || [];
    body.mccd_part2 = part2 || [];
  }
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/death/cause/`,
    body,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Record where a deceased patient's body was released — mortuary / police /
 * family (emergency-death-registration ticket 03). For a medico-legal death the
 * server refuses "family": the body is evidence.
 */
export async function recordBodyHandling(visitId, { bodyReleasedTo, overrideReason } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/death/body/`,
    {
      body_released_to: bodyReleasedTo,
      ...(overrideReason ? { override_reason: overrideReason } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * Issue the registrar's death certificate — number + optional document reference
 * (emergency-death-registration ticket 04). Refused for a medico-legal death,
 * whose certificate is withheld pending post-mortem.
 */
export async function issueDeathCertificate(visitId, { certificateNumber, certificateDocument } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/death/certificate/`,
    {
      certificate_number: certificateNumber,
      ...(certificateDocument ? { certificate_document: certificateDocument } : {}),
    },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/** Set a deceased-checklist flag the record owns — the death summary shared. */
export async function setDeathChecklist(visitId, { deathSummaryShared } = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/death/checklist/`,
    { death_summary_shared: deathSummaryShared },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return unwrap(res).data;
}

/**
 * The hospital's unified death register — one list spanning inpatient and
 * emergency deaths, each row tagged by source (emergency-death-registration
 * ticket 05).
 */
export async function getDeathRegister() {
  const res = await axios.get(`${BASE}emergency/death-register/`, { headers: authHeaders() });
  const body = unwrap(res);
  return { rows: body.data || [], count: body.count ?? 0 };
}

/** One visit's full record. */
export async function getEmergencyVisit(visitId) {
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/`, {
    headers: authHeaders(),
  });
  return unwrap(res).data;
}

/**
 * Update a visit — completing an identity, editing payer details, or
 * dismissing a duplicate warning. Only send what changed; omitted fields keep
 * their current value.
 */
export async function updateEmergencyVisit(visitId, patch) {
  const res = await axios.patch(`${BASE}emergency/visits/${visitId}/`, patch, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return unwrap(res).data;
}

/**
 * Register an arriving patient (ER-REG-*).
 *
 * Only gender and chief complaint are always required. A patient who cannot
 * give a name is registered with an identificationMark instead; the backend
 * issues the temporary label, the hospital identifier and the visit number.
 */
export async function registerEmergencyPatient({
  patientName,
  identificationMark,
  age,
  gender,
  arrivalMode,
  chiefComplaint,
  payer,
  mlcFlag,
  patientId,
}) {
  const body = {
    patient_name: patientName || undefined,
    ...(mlcFlag ? { mlc_flag: true } : {}),
    // Set only when the clerk chose to reuse an existing patient offered by the
    // Aadhaar/ABHA fast-path — the visit attaches to that record rather than a
    // new one being minted. Absent on the ordinary and unidentified paths.
    ...(patientId ? { patient_id: patientId } : {}),
    identification_mark: identificationMark || undefined,
    age: age === "" || age == null ? undefined : Number(age),
    gender,
    arrival_mode: arrivalMode,
    chief_complaint: chiefComplaint,
  };
  if (payer && Object.values(payer).some(Boolean)) body.payer = payer;

  const res = await axios.post(`${BASE}emergency/visits/`, body, {
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return unwrap(res).data;
}

/* ------------------------------------------------------------------ */
/* Department setup                                                    */
/* ------------------------------------------------------------------ */

const json = { "Content-Type": "application/json" };

/** Everything the Department Setup screen renders, in one call. */
export async function getEmergencySetup() {
  const res = await axios.get(`${BASE}emergency/setup/`, { headers: authHeaders() });
  return unwrap(res).data;
}

export async function addBedCategory(name) {
  const res = await axios.post(`${BASE}emergency/bed-categories/`, { name },
    { headers: authHeaders(json) });
  return unwrap(res).data;
}

export async function updateBedCategory(id, patch) {
  const res = await axios.patch(`${BASE}emergency/bed-categories/${id}/`, patch,
    { headers: authHeaders(json) });
  return unwrap(res).data;
}

/** Deactivates rather than deletes — beds and their history survive it. */
export async function deactivateBedCategory(id) {
  const res = await axios.delete(`${BASE}emergency/bed-categories/${id}/`,
    { headers: authHeaders() });
  return unwrap(res).data;
}

/**
 * What a bed of this category costs.
 *
 * Creates the catalogue service the first time and edits its fee afterwards —
 * a hospital prices its own emergency beds here rather than needing rows seeded
 * for it. A price change applies from then on; charges already captured keep
 * the amount they were posted at.
 */
export async function setBedCategoryCharge(id, { fee, name, unit } = {}) {
  const res = await axios.post(
    `${BASE}emergency/bed-categories/${id}/charge/`,
    { fee, ...(name ? { name } : {}), ...(unit ? { unit } : {}) },
    { headers: authHeaders(json) }
  );
  return unwrap(res).data;
}

/** Unlinks the charge from the category; the catalogue row itself stays. */
export async function clearBedCategoryCharge(id) {
  const res = await axios.delete(`${BASE}emergency/bed-categories/${id}/charge/`, {
    headers: authHeaders(),
  });
  return unwrap(res).data;
}

/**
 * The department's own fees — registration, consultation, specialist — and its
 * list of procedures and equipment, plus whether the caller may price them.
 */
export async function getEdServices() {
  const res = await axios.get(`${BASE}emergency/services/`, { headers: authHeaders() });
  return unwrap(res).data;
}

/** Price a fixed fee ({code, fee}) or add a procedure/equipment item ({kind, name, fee}). */
export async function saveEdService(payload) {
  const res = await axios.post(`${BASE}emergency/services/`, payload, { headers: authHeaders(json) });
  return unwrap(res).data;
}

/** Rename, reprice or (de)activate one procedure/equipment item. */
export async function updateEdService(serviceId, patch) {
  const res = await axios.patch(`${BASE}emergency/services/${serviceId}/`, patch, {
    headers: authHeaders(json),
  });
  return unwrap(res).data;
}

export async function addBed({ label, categoryId }) {
  const res = await axios.post(`${BASE}emergency/beds/`,
    { label, category_id: categoryId }, { headers: authHeaders(json) });
  return unwrap(res).data;
}

export async function updateBed(id, patch) {
  const res = await axios.patch(`${BASE}emergency/beds/${id}/`, patch,
    { headers: authHeaders(json) });
  return unwrap(res).data;
}

export async function removeBed(id) {
  const res = await axios.delete(`${BASE}emergency/beds/${id}/`, { headers: authHeaders() });
  return unwrap(res);
}

/** kind is "body-parts" or "wound-types". */
export async function addInjuryTerm(kind, name) {
  const res = await axios.post(`${BASE}emergency/${kind}/`, { name },
    { headers: authHeaders(json) });
  return unwrap(res).data;
}

export async function removeInjuryTerm(kind, id) {
  const res = await axios.delete(`${BASE}emergency/${kind}/${id}/`, { headers: authHeaders() });
  return unwrap(res);
}
